import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { EventCard } from '../components/events/EventCard';
import { Layout } from '../components/layout/Layout';
import { Search, ArrowRight, ArrowUp, Music, Mic2, Trophy, Heart, Sparkles, MapPin, Navigation, Loader2, PartyPopper, Theater, UtensilsCrossed, Briefcase, Award, Clock, Calendar } from 'lucide-react';
import { getImageUrl as getImageUrlUtil } from '../utils/apiUrl';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const categories = [
  { 
    id: 'music', 
    name: 'Music', 
    icon: Music,
    image: 'https://images.unsplash.com/photo-1709731191876-899e32264420?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxjb25jZXJ0JTIwY3Jvd2QlMjBuaWdodCUyMGxpZ2h0c3xlbnwwfHx8fDE3NzA0MjUxMDJ8MA&ixlib=rb-4.1.0&q=85',
    description: 'Live concerts & festivals',
    linkCategory: 'festivals'
  },
  { 
    id: 'comedy', 
    name: 'Comedy', 
    icon: Mic2,
    image: 'https://images.unsplash.com/photo-1641903806973-17eaf2d2634f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxzdGFuZCUyMHVwJTIwY29tZWR5JTIwc3RhZ2UlMjBzcG90bGlnaHR8ZW58MHx8fHwxNzcwNDI1MTA1fDA&ixlib=rb-4.1.0&q=85',
    description: 'Stand-up & shows'
  },
  { 
    id: 'sports', 
    name: 'Sports', 
    icon: Trophy,
    image: 'https://images.unsplash.com/photo-1765130729366-b54d7b2c8ea2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxzb2NjZXIlMjBtYXRjaCUyMHN0YWRpdW0lMjBuaWdodHxlbnwwfHx8fDE3NzA0MjUxMTB8MA&ixlib=rb-4.1.0&q=85',
    description: 'Games & matches'
  },
  { 
    id: 'festivals', 
    name: 'Festivals', 
    icon: PartyPopper,
    image: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&q=80&w=800',
    description: 'Live concerts & festivals'
  },
  { 
    id: 'nightlife', 
    name: 'Nightlife', 
    icon: Sparkles,
    image: 'https://images.unsplash.com/photo-1766650585178-b1ecb1763826?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxuaWdodGNsdWIlMjBkaiUyMHBhcnR5JTIwdmliZXxlbnwwfHx8fDE3NzA0MjUxMTJ8MA&ixlib=rb-4.1.0&q=85',
    description: 'Clubs & parties'
  },
  { 
    id: 'awards', 
    name: 'Awards', 
    icon: Award,
    image: 'https://images.unsplash.com/photo-1532635241-17e820acc59f?auto=format&fit=crop&q=80&w=800',
    description: 'Award & Recognition'
  },
  { 
    id: 'arts', 
    name: 'Arts & Theater', 
    icon: Theater,
    image: 'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?auto=format&fit=crop&q=80&w=800',
    description: 'Exhibitions & plays'
  },
  { 
    id: 'food', 
    name: 'Food & Drink', 
    icon: UtensilsCrossed,
    image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&q=80&w=800',
    description: 'Tastings & culinary events'
  },
  { 
    id: 'business', 
    name: 'Business', 
    icon: Briefcase,
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80&w=800',
    description: 'Networking & conferences'
  },
  { 
    id: 'weddings', 
    name: 'Weddings', 
    icon: Heart,
    image: 'https://images.unsplash.com/flagged/photo-1574298967939-b46c2c88cdc1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwd2VkZGluZyUyMGNlbGVicmF0aW9uJTIwZGFuY2luZ3xlbnwwfHx8fDE3NzA0MjUxMDl8MA&ixlib=rb-4.1.0&q=85',
    description: 'Celebrations of love'
  },
];

// Nearby Event Card with distance badge
const NearbyEventCard = ({ event }) => {
  // Safe parsing with fallbacks
  let eventDate;
  try {
    eventDate = new Date(event.date);
    if (isNaN(eventDate.getTime())) {
      eventDate = new Date(); // fallback to now
    }
  } catch (e) {
    eventDate = new Date();
  }
  
  const ticketTypes = event.ticket_types || [];
  const prices = ticketTypes.map(t => t.price).filter(p => typeof p === 'number' && !isNaN(p));
  const lowestPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const hasFreeTickets = ticketTypes.some(t => t.is_free);
  
  const CURRENCY_MAP = {
    'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh', 'ZAR': 'R',
    'CAD': 'CA$', 'USD': '$', 'GBP': '£', 'EUR': '€'
  };
  const cs = CURRENCY_MAP[event.currency] || '₦';
  
  // Determine display based on event_type
  const getEventPriceDisplay = () => {
    // For recurring events, show price range starting price
    if (event.is_recurring) {
      if (event.price_range && event.price_range.min_price > 0) {
        return <span>From {cs}{event.price_range.min_price.toLocaleString()}</span>;
      } else if (event.price_range && event.price_range.max_price > 0) {
        return <span>Up to {cs}{event.price_range.max_price.toLocaleString()}</span>;
      }
      return <span className="text-muted-foreground">See Details</span>;
    }
    
    // Non-recurring events
    if (event.event_type === 'free') {
      return <span className="text-secondary">Free</span>;
    } else if (event.event_type === 'reservation') {
      return <span className="text-blue-400">RSVP Required</span>;
    } else if (hasFreeTickets) {
      return <span className="text-secondary">Free</span>;
    } else {
      return <span>{cs}{lowestPrice.toLocaleString()}</span>;
    }
  };

  // Use centralized image URL utility
  const getImageUrl = (url) => getImageUrlUtil(url, 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=800');

  return (
    <Link 
      to={`/events/${event.event_id}`}
      className="block rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all"
      data-testid={`nearby-event-${event.event_id}`}
    >
      {/* Image */}
      <div className="aspect-[16/10] relative overflow-hidden">
        <img
          src={getImageUrl(event.image_url)}
          alt={event.title}
          className="w-full h-full object-cover transition-transform hover:scale-105"
          loading="lazy"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=800';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Distance Badge */}
        {event.distance_km !== undefined && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-sm font-medium">
            <MapPin className="w-3.5 h-3.5" />
            <span>{event.distance_km} km away</span>
          </div>
        )}

        {/* Date badge - only show for non-recurring events */}
        {!event.is_recurring && eventDate && (
          <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm rounded-lg px-2 py-1 text-center">
            <div className="text-xs font-medium text-primary">
              {format(eventDate, 'MMM')}
            </div>
            <div className="text-lg font-bold leading-tight">
              {format(eventDate, 'd')}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-2 line-clamp-1">
          {event.title}
        </h3>

        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <MapPin className="w-4 h-4 flex-shrink-0" />
          <span className="line-clamp-1">{event.venue}, {event.city}</span>
        </div>

        {/* Time/Schedule - show time for regular events, days for recurring */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Clock className="w-4 h-4 flex-shrink-0" />
          {event.is_recurring ? (
            <span>
              {event.recurring_schedule?.days?.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ') || 'Multiple days'}
            </span>
          ) : eventDate ? (
            <span>{format(eventDate, 'h:mm a')}</span>
          ) : (
            <span>Time TBD</span>
          )}
        </div>

        {/* Price */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <span className="text-xs text-muted-foreground">{event.category}</span>
          <div className="font-mono font-medium">
            {getEventPriceDisplay()}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default function Landing() {
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [nearbyEvents, setNearbyEvents] = useState([]);
  const [heroMedia, setHeroMedia] = useState(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [promoBanners, setPromoBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Detect mobile browser
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Default fallback image for hero section
  const HERO_FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=1920';

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle video play button click
  const handlePlayVideo = () => {
    if (videoRef.current) {
      videoRef.current.play()
        .then(() => {
          setVideoPlaying(true);
        })
        .catch((err) => {
          setVideoFailed(true);
        });
    }
  };

  // Simple video state management
  useEffect(() => {
    const video = videoRef.current;
    if (!video || heroMedia?.media_type !== 'video') return;

    // Track actual playing state via events
    const handlePlay = () => setVideoPlaying(true);
    const handlePause = () => setVideoPlaying(false);
    const handleEnded = () => {
      // Loop the video
      video.currentTime = 0;
      video.play().catch(() => {});
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('playing', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    // Initial autoplay attempt (silent fail is ok)
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay blocked - user needs to click play button
        setVideoPlaying(false);
      });
    }

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('playing', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [heroMedia]);

  // Use centralized image URL utility
  const getImageUrl = (url, fallback = 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=800') => getImageUrlUtil(url, fallback);

  // Fetch hero media and promo banners from database
  useEffect(() => {
    const fetchLandingMedia = async () => {
      try {
        // Fetch hero media
        const heroResponse = await axios.get(`${API_URL}/api/landing-media?section=hero&active_only=true`);
        if (heroResponse.data && heroResponse.data.length > 0) {
          setHeroMedia(heroResponse.data[0]);
        }
        
        // Fetch promotional banners
        const promoResponse = await axios.get(`${API_URL}/api/landing-media?section=promo&active_only=true`);
        if (promoResponse.data && promoResponse.data.length > 0) {
          setPromoBanners(promoResponse.data);
        }
      } catch (error) {
        console.error('Error fetching landing media:', error);
      }
    };
    fetchLandingMedia();
  }, []);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events/featured?limit=6`);
        setFeaturedEvents(response.data);
      } catch (error) {
        console.error('Error fetching featured events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  // Get user location and fetch nearby events
  useEffect(() => {
    let isMounted = true;
    
    const fetchNearbyEvents = async (lat, lng) => {
      if (!isMounted) return;
      
      try {
        const response = await axios.get(
          `${API_URL}/api/events/nearby?lat=${lat}&lng=${lng}&radius=100&limit=6`
        );
        if (isMounted) {
          setNearbyEvents(response.data);
        }
      } catch (error) {
        console.error('Error fetching nearby events:', error);
      } finally {
        if (isMounted) {
          setNearbyLoading(false);
        }
      }
    };
    
    if ('geolocation' in navigator) {
      setNearbyLoading(true);
      
      // Use a timeout to prevent iOS from hanging
      const timeoutId = setTimeout(() => {
        if (isMounted) {
          setNearbyLoading(false);
          setLocationError('Location request timed out');
        }
      }, 15000);
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          if (!isMounted) return;
          
          const { latitude, longitude } = position.coords;
          // Validate coordinates
          if (typeof latitude === 'number' && typeof longitude === 'number' && 
              !isNaN(latitude) && !isNaN(longitude)) {
            setUserLocation({ lat: latitude, lng: longitude });
            fetchNearbyEvents(latitude, longitude);
          } else {
            console.error('Invalid coordinates received');
            setNearbyLoading(false);
            setLocationError('Invalid location data');
          }
        },
        (error) => {
          clearTimeout(timeoutId);
          if (!isMounted) return;
          
          setLocationError(error.message);
          setNearbyLoading(false);
        },
        { 
          enableHighAccuracy: false, // Changed to false for faster response on iOS
          timeout: 10000, 
          maximumAge: 600000 // Cache for 10 minutes
        }
      );
    } else {
      setLocationError('Geolocation not supported');
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    if (searchQuery.trim()) {
      navigate(`/events?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Fetch search suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSearchSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      
      setLoadingSuggestions(true);
      try {
        const response = await axios.get(`${API_URL}/api/events/search/suggestions`, {
          params: { q: searchQuery, limit: 6 }
        });
        setSearchSuggestions(response.data);
        setShowSuggestions(response.data.length > 0);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSearchSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    setSearchQuery('');
    navigate(`/events/${suggestion.id}`);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Video/Image - Dynamic from database */}
        <div className="absolute inset-0 z-0">
          {heroMedia?.media_type === 'video' && !videoFailed ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster={HERO_FALLBACK_IMAGE}
                className="w-full h-full object-cover"
                key={heroMedia.url}
                style={{ objectFit: 'cover' }}
                onError={() => {
                  setVideoFailed(true);
                }}
              >
                <source src={getImageUrlUtil(heroMedia.url)} type="video/mp4" />
              </video>
              
              {/* Play button overlay - show if video not playing */}
              {!videoPlaying && (
                <button
                  onClick={handlePlayVideo}
                  className="absolute inset-0 z-20 flex items-center justify-center bg-black/30"
                  aria-label="Play video"
                  data-testid="video-play-btn"
                >
                  <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 text-primary ml-1">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </button>
              )}
              
              {/* Fallback image that shows while video loads or on error */}
              <img
                src={HERO_FALLBACK_IMAGE}
                alt="Concert crowd"
                className="absolute inset-0 w-full h-full object-cover -z-10"
                crossOrigin="anonymous"
              />
            </>
          ) : heroMedia?.media_type === 'image' ? (
            <img
              src={getImageUrlUtil(heroMedia.url)}
              alt="Hero background"
              className="w-full h-full object-cover"
              crossOrigin="anonymous"
              onError={(e) => {
                e.target.src = HERO_FALLBACK_IMAGE;
              }}
            />
          ) : (
            /* Default fallback - always show still image when no media or video failed */
            <img
              src={HERO_FALLBACK_IMAGE}
              alt="Concert crowd"
              className="w-full h-full object-cover"
              crossOrigin="anonymous"
            />
          )}
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black mb-6 text-white animate-fade-in-up">
            Your <span className="text-gradient">Next Gig</span> Awaits
          </h1>
          <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-2xl mx-auto animate-fade-in-up stagger-1">
            Discover and book tickets to the hottest events in Africa. From concerts to comedy shows, we've got you covered.
          </p>

          {/* Search Form with Suggestions */}
          <form 
            onSubmit={handleSearch}
            className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto animate-fade-in-up stagger-2"
          >
            <div className="relative flex-1" ref={searchRef}>
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
              <Input
                type="text"
                placeholder="Search events, artists, venues..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && searchSuggestions.length > 0 && setShowSuggestions(true)}
                className="h-14 pl-12 pr-4 rounded-full bg-white/10 backdrop-blur-xl border-white/20 text-white placeholder:text-white/60 focus:bg-white/20"
                data-testid="hero-search-input"
              />
              
              {/* Search Suggestions Dropdown */}
              {showSuggestions && (
                <div 
                  ref={suggestionsRef}
                  className="absolute top-full left-0 right-0 mt-2 bg-background/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-border overflow-hidden z-50"
                >
                  {loadingSuggestions ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                  ) : (
                    <div className="max-h-80 overflow-y-auto">
                      {searchSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          type="button"
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors text-left"
                          data-testid={`suggestion-${suggestion.id}`}
                        >
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                            {suggestion.image_url ? (
                              <img 
                                src={getImageUrl(suggestion.image_url, 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=100')} 
                                alt={suggestion.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=100';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                <Calendar className="w-5 h-5" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{suggestion.title}</p>
                            <p className="text-sm text-muted-foreground truncate">{suggestion.subtitle}</p>
                          </div>
                          {suggestion.is_recurring && (
                            <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">
                              Recurring
                            </span>
                          )}
                        </button>
                      ))}
                      
                      {/* View all results link */}
                      <button
                        type="submit"
                        className="w-full p-3 text-center text-primary hover:bg-muted/50 transition-colors border-t border-border"
                      >
                        View all results for "{searchQuery}"
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button 
              type="submit" 
              className="btn-secondary h-14 px-8"
              data-testid="hero-search-btn"
            >
              Find Events
            </Button>
          </form>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">Browse by Category</h2>
              <p className="text-muted-foreground">Find events that match your vibe</p>
            </div>
            <Link 
              to="/events" 
              className="hidden sm:flex items-center gap-2 text-primary hover:underline font-medium"
              data-testid="view-all-categories"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.slice(0, 5).map((category, index) => {
              const Icon = category.icon;
              const linkTo = category.linkCategory 
                ? `/events?category=${category.linkCategory}` 
                : `/events?category=${category.id}`;
              return (
                <Link
                  key={category.id}
                  to={linkTo}
                  className={`category-card group rounded-2xl overflow-hidden aspect-[4/5] relative animate-fade-in-up stagger-${index + 1}`}
                  data-testid={`category-${category.id}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=800';
                    }}
                  />
                  <div className="absolute inset-0 z-10 flex flex-col justify-end p-4">
                    <div className="bg-background/80 backdrop-blur-sm rounded-xl p-3 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-5 h-5 text-primary" />
                        <span className="font-semibold">{category.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          
          {/* Additional categories row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {categories.slice(5).map((category, index) => {
              const Icon = category.icon;
              const linkTo = category.linkCategory 
                ? `/events?category=${category.linkCategory}` 
                : `/events?category=${category.id}`;
              return (
                <Link
                  key={category.id}
                  to={linkTo}
                  className={`category-card group rounded-2xl overflow-hidden aspect-[3/2] relative animate-fade-in-up stagger-${index + 1}`}
                  data-testid={`category-${category.id}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=800';
                    }}
                  />
                  <div className="absolute inset-0 z-10 flex flex-col justify-end p-4">
                    <div className="bg-background/80 backdrop-blur-sm rounded-xl p-3 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-5 h-5 text-primary" />
                        <span className="font-semibold">{category.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Promotional Banners Section */}
      {promoBanners.length > 0 && (
        <section className="py-8 md:py-12 px-4 md:px-8 bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {promoBanners.map((banner, index) => {
                const BannerWrapper = banner.link_url ? 'a' : Link;
                const bannerProps = banner.link_url 
                  ? { href: banner.link_url, target: '_blank', rel: 'noopener noreferrer' }
                  : { to: '/events' };
                
                return (
                  <BannerWrapper
                    key={banner.media_id}
                    {...bannerProps}
                    className={`group relative rounded-2xl overflow-hidden aspect-[16/9] md:aspect-[2/1] block ${
                      promoBanners.length === 1 ? 'md:col-span-2 lg:col-span-3' : 
                      promoBanners.length === 2 && index === 0 ? 'lg:col-span-2' : ''
                    } animate-fade-in-up`}
                    data-testid={`promo-banner-${banner.media_id}`}
                  >
                    {banner.media_type === 'video' ? (
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        preload="auto"
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        style={{ objectFit: 'cover' }}
                      >
                        <source src={banner.url} type="video/mp4; codecs=avc1.42E01E,mp4a.40.2" />
                        <source src={banner.url} type="video/mp4" />
                      </video>
                    ) : (
                      <img
                        src={banner.url}
                        alt={banner.title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        crossOrigin="anonymous"
                        loading="lazy"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-secondary" />
                        <span className="text-xs font-medium text-secondary uppercase tracking-wider">Featured</span>
                      </div>
                      <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-white mb-2">{banner.title}</h3>
                      <span className="inline-flex items-center gap-1 text-sm text-white/80 group-hover:text-white transition-colors">
                        Explore Now <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </BannerWrapper>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Events Near Me Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-6 h-6 text-primary" />
                <h2 className="text-2xl sm:text-3xl font-bold">Events Near You</h2>
              </div>
              <p className="text-muted-foreground">
                {userLocation 
                  ? 'Discover events happening close to your location'
                  : 'Enable location to find events near you'
                }
              </p>
            </div>
            {nearbyEvents.length > 0 && (
              <Link 
                to="/events" 
                className="hidden sm:flex items-center gap-2 text-primary hover:underline font-medium"
                data-testid="view-all-nearby"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {nearbyLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Finding events near you...</p>
            </div>
          ) : locationError ? (
            <div className="text-center py-12 px-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Navigation className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Location Access Needed</h3>
              <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                Enable location access in your browser to discover events happening near you.
              </p>
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()}
                data-testid="retry-location-btn"
              >
                Try Again
              </Button>
            </div>
          ) : nearbyEvents && nearbyEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {nearbyEvents.map((event, index) => {
                // Skip invalid events
                if (!event || !event.event_id) return null;
                
                return (
                  <div key={event.event_id} className={`animate-fade-in-up stagger-${index + 1}`}>
                    <NearbyEventCard event={event} />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No nearby events found</h3>
              <p className="text-muted-foreground mb-4">
                There are no upcoming events within 100km of your location.
              </p>
              <Link to="/events">
                <Button className="btn-primary" data-testid="browse-all-events-btn">
                  Browse All Events
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Featured Events Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">Featured Events</h2>
              <p className="text-muted-foreground">Don't miss out on these upcoming shows</p>
            </div>
            <Link 
              to="/events" 
              className="hidden sm:flex items-center gap-2 text-primary hover:underline font-medium"
              data-testid="view-all-events"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-card border border-border overflow-hidden">
                  <div className="aspect-[16/10] skeleton" />
                  <div className="p-4 space-y-3">
                    <div className="h-6 skeleton rounded w-3/4" />
                    <div className="h-4 skeleton rounded w-1/2" />
                    <div className="h-4 skeleton rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredEvents.map((event, index) => (
                <div key={event.event_id} className={`animate-fade-in-up stagger-${index + 1}`}>
                  <EventCard event={event} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No events available yet.</p>
              <Link to="/register">
                <Button className="btn-primary" data-testid="create-first-event-btn">
                  Be the first to create an event
                </Button>
              </Link>
            </div>
          )}

          <div className="text-center mt-8 sm:hidden">
            <Link to="/events">
              <Button variant="outline" className="rounded-full" data-testid="mobile-view-all-events">
                View All Events <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
            Ready to host your own event?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of organizers who trust Next Gig to sell tickets and promote their events. Get started in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="btn-primary" data-testid="cta-create-event">
                Start Creating Events
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="rounded-full h-12 px-8" data-testid="cta-learn-more">
                Learn More
              </Button>
            </Link>
          </div>

          {/* Why NextGig — Organizer Value Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-16 max-w-6xl mx-auto text-left" data-testid="organizer-value-tiles">
            <div className="rounded-2xl border border-border bg-card p-6 space-y-2">
              <p className="text-3xl font-bold text-primary">5.5%</p>
              <p className="font-semibold text-sm">Platform fee</p>
              <p className="text-sm text-muted-foreground">The lowest in the market. Others charge 8% plus ₦100 per ticket.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6 space-y-2">
              <p className="text-3xl font-bold text-primary">Free</p>
              <p className="font-semibold text-sm">Cost to start</p>
              <p className="text-sm text-muted-foreground">Host up to one event per month at no cost. No upfront fees, no commitment.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6 space-y-2">
              <p className="text-base font-bold">Revenue dashboard built in</p>
              <p className="font-semibold text-sm">Visibility</p>
              <p className="text-sm text-muted-foreground">Track ticket sales, revenue, and attendance performance in real time from your organizer dashboard.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6 space-y-2">
              <p className="text-3xl font-bold text-primary">140+</p>
              <p className="font-semibold text-sm">Reach</p>
              <p className="text-sm text-muted-foreground">Countries where your event is accessible. List once, reach a global audience.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6 space-y-2">
              <p className="text-base font-bold">Stripe & Paystack — both handled</p>
              <p className="font-semibold text-sm">Payments</p>
              <p className="text-sm text-muted-foreground">Canadian and Nigerian payments processed natively. No extra accounts needed.</p>
            </div>
            <div className="rounded-2xl border-2 border-primary bg-card p-6 space-y-2 relative">
              <span className="absolute -top-3 left-4 bg-primary text-primary-foreground text-[11px] font-semibold px-3 py-0.5 rounded-full">For high-volume organizers</span>
              <p className="text-base font-bold">Subscribe when you're ready to grow</p>
              <p className="font-semibold text-sm">Scale</p>
              <p className="text-sm text-muted-foreground">Running more than one event a month? Upgrade to unlock unlimited listings.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 p-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all duration-300 ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Back to top"
        data-testid="back-to-top-btn"
      >
        <ArrowUp className="w-6 h-6" />
      </button>
    </Layout>
  );
}
